import { StateCreator } from "zustand";
import { MeetingResource } from "../api/meetings";
import { InstanceMetadata, RecurringMetadata } from "../model/meeting_metadata";
import { Meeting, fromApiMeeting } from "../model/meeting";
import { Host, fromApiHost } from "../model/host";
import { HostResource } from "../api/hosts";
import { toMeetingHostIdSchedule } from "../model/meeting_host_schedule";

// A series scope reaches the occurrences the backend rewrites: those ending
// within its look-back or later. Older ones keep whatever they had.
const SERIES_SCOPE_LOOKBACK_MS = 2 * 24 * 60 * 60 * 1000;

export interface MeetingsSlice {
  meetings: Meeting[];
  hostsById: Record<string, Host>;
  start: Date;
  end: Date;
  isLoading: boolean;
  loadMeetings: (start: Date, end: Date) => void;
  setInstanceMetadata: (meetingId: string, metadata: InstanceMetadata) => void;
  setRecurringMetadata: (
    meetingId: string,
    metadata: RecurringMetadata
  ) => void;
}

export const createMeetingsSlice: StateCreator<
  MeetingsSlice,
  [],
  [],
  MeetingsSlice
> = (set) => ({
  meetings: [],
  hostsById: {},
  start: new Date(),
  end: new Date(),
  isLoading: false,
  loadMeetings: async (start: Date, end: Date) => {
    set(() => ({ isLoading: true }));
    try {
      const [apiMeetings, apiHosts] = await Promise.all([
        MeetingResource.getList({
          start: start.toISOString(),
          end: end.toISOString(),
        }),
        HostResource.getList(),
      ]);
      const hosts = apiHosts.users.map(fromApiHost);
      const hostsById = hosts.reduce((acc, host) => {
        acc[host.id] = host;
        return acc;
      }, {} as Record<string, Host>);
      const meetingsRaw = apiMeetings.map((m) => fromApiMeeting(m, hostsById));
      const seen = new Set<string>();
      const meetings = meetingsRaw.filter((m) => {
        if (seen.has(m.id)) return false;
        seen.add(m.id);
        return true;
      });
      meetings.sort((a, b) => a.start.getTime() - b.start.getTime());
      set(() => ({ meetings, hostsById, start, end }));
    } finally {
      set(() => ({ isLoading: false }));
    }
  },
  setInstanceMetadata: async (
    meetingId: string,
    metadata: InstanceMetadata
  ) => {
    await MeetingResource.update(meetingId, {
      instanceMetadata: {
        meetingType: metadata.meetingType,
        memberVisible: metadata.memberVisible,
        hostId: metadata.host ? metadata.host.id : undefined,
        continuousRoomKey: metadata.continuousRoomKey,
        partnerId: metadata.partnerId,
      },
    });
    // Optimistic update only — the backend list is cached per Lambda instance
    // (30 min TTL), so an immediate reload can return stale data and clobber
    // the values just saved. The next explicit load fetches server truth.
    set((state) => ({
      meetings: state.meetings.map((m) =>
        m.id === meetingId
          ? { ...m, host: metadata.host ?? m.host, metadata: { ...m.metadata, ...metadata } }
          : m
      ),
    }));
  },
  setRecurringMetadata: async (
    meetingId: string,
    metadata: RecurringMetadata
  ) => {
    await MeetingResource.update(meetingId, {
      recurringMetadata: {
        meetingType: metadata.meetingType,
        memberVisible: metadata.memberVisible,
        hostSchedule: metadata.hostSchedule
          ? toMeetingHostIdSchedule(metadata.hostSchedule)
          : undefined,
        continuousRoomKey: metadata.continuousRoomKey,
        partnerId: metadata.partnerId,
      },
    });
    // Optimistic update only — see setInstanceMetadata for why there is no
    // post-save reload.
    const scopeReachesFrom = Date.now() - SERIES_SCOPE_LOOKBACK_MS;
    set((state) => ({
      meetings: state.meetings.map((m) =>
        m.recurringMeeting?.id === meetingId
          ? {
              ...m,
              // A series scope reaches every upcoming occurrence (the backend
              // rewrites the ones edited on their own too).
              // A private one also loses its room key, as on the backend.
              metadata:
                metadata.partnerId !== undefined &&
                m.end.getTime() >= scopeReachesFrom
                  ? {
                      ...m.metadata,
                      partnerId: metadata.partnerId,
                      ...(metadata.partnerId.trim() && {
                        continuousRoomKey: "",
                      }),
                    }
                  : m.metadata,
              recurringMeeting: m.recurringMeeting
                ? {
                    ...m.recurringMeeting,
                    // Merged: the save sends only what the editor changes
                    // (partnerId only when it changed), so a replace would
                    // drop the rest. A private series loses its key, as on
                    // the backend.
                    metadata: {
                      ...m.recurringMeeting.metadata,
                      ...metadata,
                      ...(metadata.partnerId?.trim() && {
                        continuousRoomKey: "",
                      }),
                    },
                  }
                : m.recurringMeeting,
            }
          : m
      ),
    }));
  },
});
